import { Collapse, Button, Tooltip, Drawer, Spin } from 'antd';
import { CopyOutlined, LinkOutlined, ExportOutlined, ShareAltOutlined } from '@ant-design/icons';
import {
  RowContainer,
  RowHeading,
  RowValue,
  SummaryRowContainer,
  SummaryRowHeading,
  SummaryRowValue,
  BooleanBubbleStyled,
  HoverCopyIcon,
  HoverLinkIcon,
  SummaryRowValueText,
  IconButtonMargin,
  CopiedToClipboardBubble,
  getVal,
  getTruncatedValue,
  getFormattedValue,
  SummarySectionDivider,
  TwoColumnSummaryRow,
  getBooleanBubble,
  DrawerTitleRow,
} from './styles';
import React, { useState } from 'react';
import * as C from './constants';
import JSONViewerModal from './JSONViewerModal';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';

const { Panel } = Collapse;

const SlotQualificationDetailPanel = ({ isOpen, onClose, data, adId, handleShare, loading }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const [jsonModalVisible, setJsonModalVisible] = useState(false);
  const [jsonModalValue, setJsonModalValue] = useState(null);
  const [jsonModalTitle, setJsonModalTitle] = useState('JSON Viewer');
  const location = useLocation();
  if (!data) return null;

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1200);
  };

  const renderSummaryRow = (key, value, copyable = false, link = false, isBoolean = false, fullValue = null) => {
    let displayValue;
    if (isBoolean) {
      displayValue = getBooleanBubble(value);
    } else if (key === 'Restriction Rules') {
      displayValue = getTruncatedValue(value, 2);
    } else {
      displayValue = getFormattedValue(key, value);
    }
    const valueToCopy = fullValue !== null && fullValue !== undefined ? fullValue : value;
    return (
      <SummaryRowContainer key={key}>
        <SummaryRowHeading>{key}:</SummaryRowHeading>
        <SummaryRowValue>
          <SummaryRowValueText>{displayValue}</SummaryRowValueText>
          {copyable && valueToCopy && valueToCopy !== 'N/A' && (
            <HoverCopyIcon className="hover-copy-icon" style={{ position: 'relative' }}>
              <Tooltip title="Copy">
                <IconButtonMargin>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={() => handleCopy(key, valueToCopy)}
                    tabIndex={-1}
                  />
                  {copiedKey === key && <CopiedToClipboardBubble>Copied to Clipboard</CopiedToClipboardBubble>}
                </IconButtonMargin>
              </Tooltip>
            </HoverCopyIcon>
          )}
          {link && value && value !== 'N/A' && (
            <HoverLinkIcon className="hover-link-icon">
              <Tooltip title="Open in new tab">
                <IconButtonMargin>
                  <Button
                    icon={<LinkOutlined />}
                    size="small"
                    onClick={() => window.open(value, '_blank')}
                    tabIndex={-1}
                  />
                </IconButtonMargin>
              </Tooltip>
            </HoverLinkIcon>
          )}
        </SummaryRowValue>
      </SummaryRowContainer>
    );
  };

  const renderRow = (key, value, copyable = false, link = false, isBoolean = false, fullValue = null) => {
    let displayValue = value === undefined || value === null || value === '' ? 'N/A' : value;
    if (isBoolean) {
      const boolVal = displayValue === true || displayValue === 'true';
      displayValue = <BooleanBubbleStyled $isTrue={boolVal}>{boolVal ? 'true' : 'false'}</BooleanBubbleStyled>;
    } else if (key === 'Request Body' || key === 'Response Body') {
      displayValue = getTruncatedValue(value);
    }
    const isRequestOrResponse = key === 'Request Body' || key === 'Response Body';
    const valueToCopy = isRequestOrResponse && fullValue ? fullValue : value;
    const OpenInNewTabIcon = isRequestOrResponse ? ExportOutlined : LinkOutlined;
    const handleOpenInNewTab = () => {
      if (isRequestOrResponse && fullValue && fullValue !== 'N/A') {
        setJsonModalValue(fullValue);
        setJsonModalTitle(key);
        setJsonModalVisible(true);
      } else if (link && value && value !== 'N/A') {
        window.open(value, '_blank');
      }
    };
    return (
      <RowContainer key={key}>
        <RowHeading>{key}:</RowHeading>
        <RowValue>
          <SummaryRowValueText>{displayValue}</SummaryRowValueText>
          {copyable && valueToCopy && valueToCopy !== 'N/A' && (
            <HoverCopyIcon className="hover-copy-icon" style={{ position: 'relative' }}>
              <Tooltip title="Copy">
                <IconButtonMargin>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={() => handleCopy(key, valueToCopy)}
                    tabIndex={-1}
                  />
                  {copiedKey === key && <CopiedToClipboardBubble>Copied to Clipboard</CopiedToClipboardBubble>}
                </IconButtonMargin>
              </Tooltip>
            </HoverCopyIcon>
          )}
          {link && value && value !== 'N/A' && (
            <HoverLinkIcon className="hover-link-icon">
              <Tooltip title="Open in new tab">
                <IconButtonMargin>
                  <Button icon={<OpenInNewTabIcon />} size="small" onClick={handleOpenInNewTab} tabIndex={-1} />
                </IconButtonMargin>
              </Tooltip>
            </HoverLinkIcon>
          )}
        </RowValue>
      </RowContainer>
    );
  };

  // Summary Section (reordered to match screenshot layout)
  const summaryRows = [
    renderSummaryRow('Endpoint', getVal(data, C.SLOT_QUALIFICATION_ENDPOINT), true),
    renderSummaryRow('Ad Id', adId || getVal(data, C.SLOT_QUALIFICATION_AD_ID), true),
    renderSummaryRow('Proxy URL', getVal(data, C.SLOT_QUALIFICATION_PROXY_URL), true),
    <TwoColumnSummaryRow
      key="row1"
      leftLabel="Invariant Qualified"
      leftValue={getVal(data, C.SLOT_QUALIFICATION_INVARIANT_QUALIFIED)}
      leftIsBoolean={true}
      rightLabel="Slot Qualified"
      rightValue={getVal(data, C.SLOT_QUALIFICATION_SLOT_QUALIFIED)}
      rightIsBoolean={true}
      getBooleanBubble={getBooleanBubble}
      getFormattedValue={getFormattedValue}
    />,
    <TwoColumnSummaryRow
      key="row2"
      leftLabel="DQ Status"
      leftValue={getVal(data, C.SLOT_QUALIFICATION_DQ_STATUS)}
      leftIsDQStatus={true}
      rightLabel="Selection"
      rightValue={getVal(data, C.SLOT_QUALIFICATION_SELECTION)}
      getBooleanBubble={getBooleanBubble}
      getFormattedValue={getFormattedValue}
    />,
    <TwoColumnSummaryRow
      key="row3"
      leftLabel="Pod Position"
      leftValue={getVal(data, C.SLOT_QUALIFICATION_POD_POSITION)}
      rightLabel="Slot Position"
      rightValue={getVal(data, C.SLOT_QUALIFICATION_SLOT_POSITION)}
      getBooleanBubble={getBooleanBubble}
      getFormattedValue={getFormattedValue}
    />,
    <TwoColumnSummaryRow
      key="row4"
      leftLabel="Qualified ads"
      leftValue={getVal(data, C.SLOT_QUALIFICATION_QUALIFIED_ADS)}
      rightLabel="Filler Only"
      rightValue={getVal(data, C.SLOT_QUALIFICATION_FILLER_ONLY)}
      rightIsBoolean={true}
      getBooleanBubble={getBooleanBubble}
      getFormattedValue={getFormattedValue}
    />,
    <SummarySectionDivider key="divider-qualified-dqfilter" />, // Divider for improved spacing
    renderSummaryRow('In Next Sequence', getVal(data, C.SLOT_QUALIFICATION_IN_NEXT_SEQUENCE), false, false, true),
    renderSummaryRow(
      'Restriction Rules',
      getTruncatedValue(getVal(data, C.INVARIANT_QUALIFICATION_RESTRICTION_RULES), 2),
      true,
      false,
      false,
      getVal(data, C.INVARIANT_QUALIFICATION_RESTRICTION_RULES)
    ),
    renderSummaryRow('DQ Filters', getVal(data, C.SLOT_QUALIFICATION_DQ_FILTER)),
    renderSummaryRow('DQ Dimensions', getVal(data, C.SLOT_QUALIFICATION_DQ_DIMENSION)),
    renderSummaryRow('Ads IDs In Sequence', getVal(data, C.SLOT_QUALIFICATION_ADS_IN_SEQUENCE), true),
  ];

  const requestRows = [
    renderRow(
      'Request Body',
      getTruncatedValue(getVal(data, C.SLOT_QUALIFICATION_REQUEST_BODY)), // show truncated in panel
      true,
      true,
      false,
      getVal(data, C.SLOT_QUALIFICATION_REQUEST_BODY) // pass full value for new tab
    ),
  ];

  const responseRows = [
    renderRow(
      'Response Body',
      getTruncatedValue(getVal(data, C.SLOT_QUALIFICATION_RESPONSE_BODY)), // show truncated in panel
      true,
      true,
      false,
      getVal(data, C.SLOT_QUALIFICATION_RESPONSE_BODY) // pass full value for new tab
    ),
  ];

  // Request Metadata Section
  const metadataRows = [
    renderRow('App', getVal(data, C.SLOT_QUALIFICATION_APP)),
    renderRow('Session Type', getVal(data, C.SLOT_QUALIFICATION_SESSION_TYPE)),
    renderRow('Content Type', getVal(data, C.SLOT_QUALIFICATION_CONTENT_TYPE)),
    renderRow('Content Rating', getVal(data, C.SLOT_QUALIFICATION_CONTENT_RATING)),
  ];

  return (
    <Drawer
      title={
        <DrawerTitleRow>
          <span>Detailed Report</span>
          <Tooltip title="Copy shareable link">
            <ShareAltOutlined style={{ cursor: 'pointer', fontSize: 18 }} onClick={handleShare} />
          </Tooltip>
        </DrawerTitleRow>
      }
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={600}
      destroyOnClose
    >
      {loading && !data ? (
        <Spin tip="Loading..." style={{ width: '100%', marginTop: 40 }} />
      ) : (
        <Collapse defaultActiveKey={['summary', 'request', 'response', 'metadata']}>
          <Panel header="Summary" key="summary">
            {summaryRows}
          </Panel>
          <Panel header="Request" key="request">
            {requestRows}
          </Panel>
          <Panel header="Response" key="response">
            {responseRows}
          </Panel>
          <Panel header="Request Metadata" key="metadata">
            {metadataRows}
          </Panel>
        </Collapse>
      )}
      <JSONViewerModal
        visible={jsonModalVisible}
        onClose={() => setJsonModalVisible(false)}
        json={jsonModalValue}
        title={jsonModalTitle}
      />
    </Drawer>
  );
};

export default SlotQualificationDetailPanel;
